import { useNavigate } from 'react-router-dom'
import {
  faArrowRight,
  faPartyHorn,
  faBank,
  faEnvelope,
  faCheckSquare,
  faReceipt,
} from '@fortawesome/pro-regular-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { selectDisconnectedAccountCount } from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { defaultMessagesURL } from '@/features/zendesk/helpers'
import { getUserNeedsClarificationTransactionsSelector } from '@/features/transactions/transactions.selectors'
import { getAllActiveUserActionItems } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { selectYourTurnNumber } from '@/v2-components/conversations/conversation.slice'

import { TypographyStyles } from '@/styles'
import { Button, Card, CardColors, Icon, Text } from '@/ui'
import { isEmailVerified } from '@/selectors/user.selectors'

const SummaryLabel = ({
  onClick,
  text,
  icon,
}: {
  onClick: () => void
  text: string
  icon: IconDefinition
}) => (
  <Button onClick={onClick} variant="secondary" className="h-8">
    <Icon icon={icon} color="text-neutral-500" scale={0.75} />
    <Text variant={TypographyStyles.BODY_XS}>{text}</Text>
    <Icon icon={faArrowRight} color="text-neutral-500" scale={0.75} />
  </Button>
)

export const SummaryActions = () => {
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const messageCount = useReselector(selectYourTurnNumber)
  const userEmailVerified = useReselector(isEmailVerified)
  const activeItems = useReselector(getAllActiveUserActionItems)
  const disconnectedAccountCount = useReselector(selectDisconnectedAccountCount)
  const needsClarificationTransactions = useReselector(
    getUserNeedsClarificationTransactionsSelector
  )

  const hasNoItems =
    !messageCount &&
    !activeItems.length &&
    !disconnectedAccountCount &&
    !needsClarificationTransactions.length

  return (
    <div className="flex flex-col">
      <Text variant={TypographyStyles.LABEL_SM} className="w-full">
        Your Summary
      </Text>

      {hasNoItems ? (
        <Card color={CardColors.LEVEL_0}>
          <Text variant={TypographyStyles.BODY_SM} textAlign="center">
            <Icon icon={faPartyHorn} style={{ marginRight: 8 }} />
            You&apos;re all caught up! No unread messages, to dos or
            transactions to review.
          </Text>
        </Card>
      ) : (
        <div className="flex mt-1 gap-2">
          {Boolean(disconnectedAccountCount) && (
            <SummaryLabel
              icon={faBank}
              text={`Disconnected Accounts: (${disconnectedAccountCount})`}
              onClick={() => {
                track('Clicked Your Summary Disconnected Accounts Button')
                navigate('/accounts#connected-institutions')
              }}
            />
          )}

          {Boolean(messageCount) && userEmailVerified && (
            <SummaryLabel
              icon={faEnvelope}
              text={`Unread Messages: (${messageCount})`}
              onClick={() => {
                track('Clicked Your Summary Messages Button')
                window.location.href = defaultMessagesURL
              }}
            />
          )}

          {Boolean(activeItems.length) && (
            <SummaryLabel
              icon={faCheckSquare}
              text={`To Dos: (${activeItems.length})`}
              onClick={() => {
                track('Clicked Your Summary To Dos Button')
                window.location.href = '#dashboard-todos'
              }}
            />
          )}

          {Boolean(needsClarificationTransactions.length) && (
            <SummaryLabel
              icon={faReceipt}
              text={`Transactions to Review: (${needsClarificationTransactions.length})`}
              onClick={() => {
                track('Clicked Your Summary Transactions Button')
                navigate('/transactions/review')
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
